import { Anchor, Badge, Button, Group, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminUsers, type AdminStatus, type AdminUserDto } from '../api/admin-users';

const STATUS_BADGE: Record<AdminStatus, { color: string; label: string }> = {
  active: { color: 'green', label: 'Active' },
  pending: { color: 'yellow', label: 'Pending' },
  disabled: { color: 'gray', label: 'Disabled' },
};

export function AdminUsersListPage() {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError } = useAdminUsers();

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (u) => u.email.toLowerCase().includes(q) || u.displayName.toLowerCase().includes(q),
    );
  }, [data, search]);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Admin users</Title>
        <Button
          component={Link}
          to="/admin-users/new"
          leftSection={<IconPlus size={16} />}
          variant="filled"
        >
          Invite admin
        </Button>
      </Group>

      <TextInput
        placeholder="Search by email or name…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        w={320}
      />

      {isLoading && <Text c="dimmed">Loading…</Text>}
      {isError && <Text c="red">Failed to load admin users.</Text>}

      {!isLoading && !isError && data !== undefined && (
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th>Display name</Table.Th>
              <Table.Th style={{ width: 100 }}>Role</Table.Th>
              <Table.Th style={{ width: 120 }}>Status</Table.Th>
              <Table.Th style={{ width: 180 }}>Last login</Table.Th>
              <Table.Th style={{ width: 100 }}>Edit</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed">No admin users match.</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filtered.map((u: AdminUserDto) => {
                const badge = STATUS_BADGE[u.status];
                return (
                  <Table.Tr key={u.id}>
                    <Table.Td>{u.email}</Table.Td>
                    <Table.Td>{u.displayName}</Table.Td>
                    <Table.Td>
                      <Badge variant="light">{u.role}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={badge.color}>{badge.label}</Badge>
                    </Table.Td>
                    <Table.Td>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('en-GB') : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={`/admin-users/${u.id}/edit`}>
                        edit
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
